import { createCameraKeeper } from "./camera-keeper.js";
import type { StoredImage } from "./types.js";

export interface Captured {
  readonly image: StoredImage;
  readonly width: number;
  readonly height: number;
}

export function fitWithin(
  width: number,
  height: number,
  max = 2048,
): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export async function startCamera(
  facing: "environment" | "user",
  media: MediaDevices | undefined,
): Promise<MediaStream | null> {
  if (!media?.getUserMedia) {
    return null;
  }
  try {
    return await media.getUserMedia({ video: { facingMode: facing }, audio: false });
  } catch {
    return null;
  }
}

export type Facing = "environment" | "user";

const keeper = createCameraKeeper<Facing, MediaStream | null>((stream) => stream);
let lastFacing: Facing = "environment";

export function keptFacing(): Facing {
  return lastFacing;
}

export function keepCamera(
  facing: Facing,
  media: MediaDevices | undefined,
): Promise<MediaStream | null> {
  lastFacing = facing;
  return keeper.acquire(facing, () => startCamera(facing, media));
}

async function encode(
  source: CanvasImageSource,
  width: number,
  height: number,
  mirror: boolean,
): Promise<Captured> {
  const size = fitWithin(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("canvas unavailable");
  }
  if (mirror) {
    context.translate(size.width, 0);
    context.scale(-1, 1);
  }
  context.drawImage(source, 0, 0, size.width, size.height);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error("encode failed"))),
      "image/jpeg",
      0.85,
    ),
  );
  return { image: { bytes: await blob.arrayBuffer(), type: "image/jpeg" }, ...size };
}

export function captureVideo(video: HTMLVideoElement, mirror: boolean): Promise<Captured> {
  return encode(video, video.videoWidth, video.videoHeight, mirror);
}

export async function readFile(file: File): Promise<Captured> {
  const bitmap = await createImageBitmap(file);
  try {
    return await encode(bitmap, bitmap.width, bitmap.height, false);
  } finally {
    bitmap.close();
  }
}
